import { useRouterState } from '@tanstack/react-router';
import {
  type KeyboardEvent as ReactKeyboardEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import styles from './ContextMenu.module.css';
import { ContextMenuHeader } from './ContextMenuHeader';
import { ContextMenuItem } from './ContextMenuItem';
import { ContextMenuGroupLabel, ContextMenuSeparator } from './ContextMenuSeparator';
import { ContextMenuSub } from './ContextMenuSub';
import { useContextMenuStore } from './contextMenuStore';
import {
  type ContextMenuNode,
  type ContextMenuWidth,
  type MenuFilterCtl,
  isFocusable,
  visibleNodes,
} from './types';

/** Зазор до края вьюпорта при collision-flip. */
const VIEWPORT_PAD = 8;
/**
 * D5.5 (CM-F2) — обязательный запас по низу панели.
 *
 * Замер довеска F18: панель 946px при вьюпорте 954px. Ни одного ВКЛЮЧЁННОГО
 * пункта ниже сгиба тогда не было, поэтому дефекта не случилось — но правило
 * «панель обязана оставлять запас» в примитиве выражено не было, и следующее
 * меню длиннее упёрлось бы молча. Теперь предел высоты считается с этим
 * запасом: панель либо помещается, либо честно получает прокрутку.
 */
const PANEL_BOTTOM_CLEARANCE = 24;
/** Подменю наезжает на родителя — курсор не проваливается в щель (макет 65:8). */
const SUB_OVERLAP = 4;
/** Внутренний padding панели: верх подменю выравнивается по верху пункта минус он. */
const PANEL_PAD = 6;

export type Placement =
  | { kind: 'point'; x: number; y: number }
  | { kind: 'anchor'; itemRect: DOMRect; parentRect: DOMRect };

export type PanelPos = { left: number; top: number; maxHeight: number };

/** Вынесено из компонента ради прямых юнитов на collision-flip (F1 AC). */
export function computePosition(placement: Placement, w: number, h: number): PanelPos {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const maxHeight = vh - VIEWPORT_PAD * 2 - PANEL_BOTTOM_CLEARANCE;
  /**
   * Нижний предел — ОДИН для всех расчётов. Первая попытка урезала только
   * `maxHeight`, оставив прежнюю формулу прижатия: панель становилась ниже, но
   * прижималась к тому же краю, и весь выигрыш уходил в отступ СВЕРХУ — запаса
   * снизу как не было, так и не появлялось. Поймано юнитом на collision-flip.
   */
  const bottomLimit = vh - VIEWPORT_PAD - PANEL_BOTTOM_CLEARANCE;
  const fitTop = (desired: number): number => {
    if (desired + h <= bottomLimit) return Math.max(VIEWPORT_PAD, desired);
    return Math.max(VIEWPORT_PAD, bottomLimit - Math.min(h, maxHeight));
  };

  if (placement.kind === 'point') {
    // Вправо-вниз от курсора; не влезло — зеркалим относительно точки клика.
    let left = placement.x;
    if (left + w > vw - VIEWPORT_PAD) left = placement.x - w;
    left = Math.max(VIEWPORT_PAD, Math.min(left, vw - w - VIEWPORT_PAD));
    let top = placement.y;
    if (top + h > bottomLimit && placement.y - h >= VIEWPORT_PAD) top = placement.y - h;
    return { left, top: fitTop(top), maxHeight };
  }

  const { itemRect, parentRect } = placement;
  let left = parentRect.right - SUB_OVERLAP;
  if (left + w > vw - VIEWPORT_PAD) left = parentRect.left - w + SUB_OVERLAP;
  left = Math.max(VIEWPORT_PAD, Math.min(left, vw - w - VIEWPORT_PAD));
  return { left, top: fitTop(itemRect.top - PANEL_PAD), maxHeight };
}

type MenuPanelProps = {
  /** Функция — у подменю: оно пересчитывает состав по своей строке поиска. */
  nodes: ContextMenuNode[] | ((ctl: MenuFilterCtl) => ContextMenuNode[]);
  width: ContextMenuWidth;
  placement: Placement;
  /** Закрыть всё меню целиком (выбор пункта, Esc, клик вне). */
  closeAll: () => void;
  /** Закрыть только эту панель и вернуть фокус родительскому пункту (←). */
  closeSelf?: () => void;
  level: number;
  ariaLabel: string;
  /** Подменю, открытое с клавиатуры, забирает фокус на первый пункт (APG). */
  autoFocusFirst: boolean;
};

function MenuPanel({
  nodes: nodesInput,
  width,
  placement,
  closeAll,
  closeSelf,
  level,
  ariaLabel,
  autoFocusFirst,
}: MenuPanelProps): JSX.Element {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const [filter, setFilter] = useState('');
  // Счётчик пересборки: пункты-переключатели (метки) меняют состояние вне
  // React — без явного сигнала галочка осталась бы от момента открытия.
  const [revision, setRevision] = useState(0);
  const refresh = useCallback(() => setRevision((n) => n + 1), []);
  const allNodes =
    typeof nodesInput === 'function'
      ? nodesInput({ filter, setFilter, refresh, revision })
      : nodesInput;
  /**
   * D5.5 (CM-F2) — свёрнутые группы. Состояние живёт в панели, а не в реестре:
   * реестр отвечает за состав меню, а свёрнутость — за то, как этот состав
   * показан здесь и сейчас. Начальное значение берётся из узлов один раз.
   */
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(
    () =>
      new Set(
        allNodes
          .filter((n) => n.kind === 'group-label' && n.collapsible === true && n.defaultCollapsed)
          .map((n) => n.id),
      ),
  );
  // Список, по которому ходит клавиатура, ОБЯЗАН совпадать с отрисованным:
  // модель узлов плоская и адресуется позицией.
  const nodes = visibleNodes(allNodes, collapsed);
  const [pos, setPos] = useState<PanelPos | null>(null);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [openSub, setOpenSub] = useState<{ id: string; viaKeyboard: boolean } | null>(null);
  const typeahead = useRef<{ buffer: string; timer: ReturnType<typeof setTimeout> | null }>({
    buffer: '',
    timer: null,
  });

  const focusableIdx = nodes.reduce<number[]>((acc, node, i) => {
    if (isFocusable(node)) acc.push(i);
    return acc;
  }, []);
  /**
   * Roving tabindex: табируемым обязан быть РОВНО ОДИН пункт, даже когда меню
   * открыто мышью и активного ещё нет. Иначе внутри прокручиваемой панели нет
   * ни одного табируемого элемента — axe справедливо ловит это правилом
   * `scrollable-region-focusable` (F17), а Tab проваливается мимо меню.
   */
  const tabbableIndex = activeIndex >= 0 ? activeIndex : (focusableIdx[0] ?? -1);

  /**
   * D5.5 — позиция пересчитывается и когда панель МЕНЯЕТ ВЫСОТУ, а не только
   * при смене места вызова. Свёртка групп сделала высоту переменной, и без
   * наблюдателя развёрнутая группа уезжала за нижний край: замер показал
   * −240px, потому что `top` остался посчитанным под свёрнутую панель. Ровно
   * тот молчаливый упор, о котором предупреждала находка CM-F2.
   */
  useLayoutEffect(() => {
    const el = panelRef.current;
    if (!el) return;
    const recompute = (): void => {
      const next = computePosition(placement, el.offsetWidth, el.offsetHeight);
      // Сравнение по значениям: `placement` пересоздаётся на каждый рендер
      // родителя, и без него setPos гонял бы лишние рендеры на каждый ховер.
      setPos((prev) => {
        if (
          prev &&
          prev.left === next.left &&
          prev.top === next.top &&
          prev.maxHeight === next.maxHeight
        ) {
          return prev;
        }
        /**
         * NC-11 (свод tails-sweep-2, заход 5) — панель не уезжает из-под курсора
         * БЕЗ НУЖДЫ.
         *
         * Пересчёт нужен: без него развёрнутая группа уходила за нижний край
         * (замер D5.5 — −240px). Но он срабатывал и тогда, когда места хватало,
         * и панель прыгала вверх на высоту раскрытой группы — переключатель, по
         * которому только что кликнули, уезжал из-под указателя, и следующий
         * клик попадал в соседний пункт. Клавиатурный путь этим не задет: там
         * фокус едет вместе с панелью.
         *
         * Правило: если при СТАРОМ `top` панель по-прежнему помещается в окно —
         * держим старый. Двигаемся ровно тогда, когда иначе не влезаем.
         */
        if (prev && prev.left === next.left) {
          const fitsAtPrevTop = prev.top + el.offsetHeight <= window.innerHeight - 8;
          if (fitsAtPrevTop) {
            return prev.maxHeight === next.maxHeight
              ? prev
              : { ...prev, maxHeight: next.maxHeight };
          }
        }
        return next;
      });
    };
    recompute();
    const ro = new ResizeObserver(recompute);
    ro.observe(el);
    return () => ro.disconnect();
  }, [placement]);

  const focusedOnOpen = useRef(false);
  // biome-ignore lint/correctness/useExhaustiveDependencies: фокус — один раз за открытие
  useEffect(() => {
    if (focusedOnOpen.current) return;
    if (autoFocusFirst) {
      const first = focusableIdx[0];
      if (first !== undefined) {
        focusedOnOpen.current = true;
        setActiveIndex(first);
        return;
      }
    }
    // Меню открыто мышью: фокус на контейнер — клавиатура работает, но ни один
    // пункт не подсвечен заранее (семантика ПКМ Проводника).
    //
    // ЖДЁМ вычисленную позицию: до неё панель отрисована `visibility: hidden`,
    // а `focus()` на скрытом элементе — no-op. Из-за этого после ПКМ мышью
    // фокус оставался на строке, и стрелки ходили по таблице, а не по меню
    // (найдено e2e-прогоном F17; юниты не ловили — они шлют keydown прямо в
    // панель).
    if (!pos) return;
    focusedOnOpen.current = true;
    panelRef.current?.focus();
  }, [pos, autoFocusFirst]);

  useEffect(() => {
    if (activeIndex < 0) return;
    itemRefs.current[activeIndex]?.focus();
  }, [activeIndex]);

  const moveActive = useCallback(
    (dir: 1 | -1) => {
      if (focusableIdx.length === 0) return;
      const cur = focusableIdx.indexOf(activeIndex);
      const next = cur === -1 ? (dir === 1 ? 0 : focusableIdx.length - 1) : cur + dir;
      const wrapped = (next + focusableIdx.length) % focusableIdx.length;
      const target = focusableIdx[wrapped];
      if (target !== undefined) setActiveIndex(target);
    },
    [activeIndex, focusableIdx],
  );

  const activate = useCallback(
    (index: number, viaKeyboard: boolean) => {
      const node = nodes[index];
      if (!node || !isFocusable(node)) return;
      if (node.kind === 'group-label') {
        // Свёртка не закрывает меню: это управление показом, а не выбор.
        setCollapsed((prev) => {
          const next = new Set(prev);
          if (next.has(node.id)) next.delete(node.id);
          else next.add(node.id);
          return next;
        });
        return;
      }
      if (node.disabled) return;
      if (node.kind === 'sub') {
        setOpenSub({ id: node.id, viaKeyboard });
        return;
      }
      node.onSelect();
      if (node.closeOnSelect === false) return;
      closeAll();
    },
    [nodes, closeAll],
  );

  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        e.stopPropagation();
        moveActive(1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        e.stopPropagation();
        moveActive(-1);
        break;
      case 'Home': {
        e.preventDefault();
        const first = focusableIdx[0];
        if (first !== undefined) setActiveIndex(first);
        break;
      }
      case 'End': {
        e.preventDefault();
        const last = focusableIdx[focusableIdx.length - 1];
        if (last !== undefined) setActiveIndex(last);
        break;
      }
      case 'ArrowRight': {
        const node = nodes[activeIndex];
        if (node && node.kind === 'sub' && !node.disabled) {
          e.preventDefault();
          e.stopPropagation();
          setOpenSub({ id: node.id, viaKeyboard: true });
        }
        break;
      }
      case 'ArrowLeft':
        if (closeSelf) {
          e.preventDefault();
          e.stopPropagation();
          closeSelf();
        }
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        e.stopPropagation();
        activate(activeIndex, true);
        break;
      case 'Escape':
        e.preventDefault();
        e.stopPropagation();
        closeAll();
        break;
      case 'Tab':
        e.preventDefault();
        closeAll();
        break;
      default: {
        if (e.key.length !== 1 || e.metaKey || e.ctrlKey || e.altKey) return;
        const buf = typeahead.current;
        buf.buffer += e.key.toLowerCase();
        if (buf.timer) clearTimeout(buf.timer);
        buf.timer = setTimeout(() => {
          buf.buffer = '';
        }, 600);
        const start = focusableIdx.indexOf(activeIndex) + 1;
        for (let i = 0; i < focusableIdx.length; i++) {
          const probe = focusableIdx[(start + i) % focusableIdx.length];
          if (probe === undefined) continue;
          const node = nodes[probe];
          if (!node || !isFocusable(node)) continue;
          if (node.kind !== 'group-label' && node.disabled) continue;
          if (node.label.toLowerCase().startsWith(buf.buffer)) {
            setActiveIndex(probe);
            break;
          }
        }
      }
    }
  };

  const openSubNode = openSub ? nodes.find((n) => n.kind === 'sub' && n.id === openSub.id) : null;
  const openSubIndex = openSub ? nodes.findIndex((n) => isFocusable(n) && n.id === openSub.id) : -1;
  const subItemEl = openSubIndex >= 0 ? itemRefs.current[openSubIndex] : null;

  return (
    <>
      <div
        ref={panelRef}
        data-ctxmenu-panel={level}
        role="menu"
        aria-label={ariaLabel}
        tabIndex={-1}
        className={styles.panel}
        data-width={width}
        style={{
          left: pos?.left ?? 0,
          top: pos?.top ?? 0,
          maxHeight: pos?.maxHeight,
          visibility: pos ? 'visible' : 'hidden',
        }}
        onKeyDown={onKeyDown}
        onContextMenu={(e) => e.preventDefault()}
      >
        {nodes.map((node, index) => {
          switch (node.kind) {
            case 'separator':
              return <ContextMenuSeparator key={node.id} />;
            case 'group-label':
              return (
                <ContextMenuGroupLabel
                  key={node.id}
                  label={node.label}
                  count={node.count}
                  collapsible={node.collapsible === true}
                  collapsed={collapsed.has(node.id)}
                  active={activeIndex === index}
                  tabbable={tabbableIndex === index}
                  itemRef={(el) => {
                    itemRefs.current[index] = el;
                  }}
                  onToggle={() => {
                    setActiveIndex(index);
                    activate(index, false);
                  }}
                />
              );
            case 'header':
              return (
                <ContextMenuHeader
                  key={node.id}
                  title={node.title}
                  {...(node.hint ? { hint: node.hint } : {})}
                />
              );
            case 'custom':
              return (
                <div key={node.id} className={styles.custom}>
                  {node.render({ close: closeAll, filter, setFilter, refresh, revision })}
                </div>
              );
            case 'sub':
              return (
                <ContextMenuSub
                  key={node.id}
                  ref={(el) => {
                    itemRefs.current[index] = el;
                  }}
                  node={node}
                  active={activeIndex === index}
                  tabbable={tabbableIndex === index}
                  expanded={openSub?.id === node.id}
                  onOpenSub={() => activate(index, false)}
                  onHover={() => {
                    setActiveIndex(index);
                    if (!node.disabled) setOpenSub({ id: node.id, viaKeyboard: false });
                  }}
                />
              );
            default:
              return (
                <ContextMenuItem
                  key={node.id}
                  ref={(el) => {
                    itemRefs.current[index] = el;
                  }}
                  node={node}
                  active={activeIndex === index}
                  tabbable={tabbableIndex === index}
                  onActivate={() => activate(index, false)}
                  onHover={() => {
                    setActiveIndex(index);
                    // Уход с раскрывателя на обычный пункт закрывает подменю.
                    setOpenSub(null);
                  }}
                />
              );
          }
        })}
      </div>

      {openSubNode?.kind === 'sub' && subItemEl && panelRef.current && (
        <MenuPanel
          nodes={openSubNode.items}
          width={openSubNode.width ?? 'sub'}
          placement={{
            kind: 'anchor',
            itemRect: subItemEl.getBoundingClientRect(),
            parentRect: panelRef.current.getBoundingClientRect(),
          }}
          closeAll={closeAll}
          closeSelf={() => {
            setOpenSub(null);
            if (openSubIndex >= 0) setActiveIndex(openSubIndex);
          }}
          level={level + 1}
          ariaLabel={openSubNode.label}
          autoFocusFirst={openSub?.viaKeyboard ?? false}
        />
      )}
    </>
  );
}

/**
 * Единственный носитель ПКМ-меню в приложении (SOLUTION §6, F1).
 *
 * Монтируется один раз в `__root.tsx`. Поверхности не рендерят меню сами —
 * они кладут узлы в store через `useContextMenu()`.
 */
export function ContextMenuSurface(): JSX.Element | null {
  const payload = useContextMenuStore((s) => s.payload);
  const close = useContextMenuStore((s) => s.close);
  const href = useRouterState({ select: (s) => s.location.href });
  const openedAt = useRef<string | null>(null);

  const closeAndRestore = useCallback(() => {
    const source = payload?.source;
    close();
    // Фокус возвращается источнику — иначе после Esc он улетает на <body>
    // и клавиатурная навигация по строкам начинается заново.
    if (source?.isConnected) source.focus();
  }, [close, payload]);

  /**
   * D5.5 (CM-F2) — строка-источник подсвечена, пока меню открыто.
   *
   * Панель перекрывает строку, из которой вызвана, и при длинном меню теряется,
   * к чему относятся действия. Шапка меню закрывает это словом, но не визуальной
   * связью. Метка ставится атрибутом на сам элемент-источник, а не классом:
   * источником бывает `<tr>` таблицы, карточка канбана, бар ганта и чип —
   * общего компонента у них нет, а глобальное правило по атрибуту одно.
   */
  useEffect(() => {
    const source = payload?.source;
    if (!source) return;
    const row = source.closest('tr') ?? source;
    row.setAttribute('data-ctxmenu-source', 'true');
    return () => {
      row.removeAttribute('data-ctxmenu-source');
    };
  }, [payload]);

  // Уход роута закрывает меню: пункты ссылались на сущности прежнего экрана.
  useEffect(() => {
    if (!payload) {
      openedAt.current = null;
      return;
    }
    if (openedAt.current === null) {
      openedAt.current = href;
      return;
    }
    if (openedAt.current !== href) close();
  }, [href, payload, close]);

  useEffect(() => {
    if (!payload) return;
    const insidePanel = (target: EventTarget | null): boolean =>
      target instanceof Element && target.closest('[data-ctxmenu-panel]') !== null;

    const onPointerDown = (e: MouseEvent) => {
      if (!insidePanel(e.target)) close();
    };
    const onScroll = (e: Event) => {
      if (!insidePanel(e.target)) close();
    };
    const onResize = () => close();
    const onKeyDown = (e: KeyboardEvent) => {
      // Esc ловим и на document: фокус мог остаться на источнике (открытие мышью).
      if (e.key !== 'Escape') return;
      // F5 — контракт Esc-иерархии: меню верхнее, поэтому помечает нажатие
      // обработанным. Иначе тот же Esc доходил до peek-панели и закрывал её
      // заодно (открытие меню мышью — фокус вне панели, гасить было нечем).
      e.preventDefault();
      closeAndRestore();
    };
    document.addEventListener('mousedown', onPointerDown, true);
    // capture: до слушателей нижних слоёв на том же `document` (peek-панель).
    document.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('mousedown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [payload, close, closeAndRestore]);

  if (!payload || typeof document === 'undefined') return null;

  return createPortal(
    <MenuPanel
      key={`${payload.x}:${payload.y}:${payload.ariaLabel}`}
      nodes={payload.nodes}
      width={payload.width}
      placement={{ kind: 'point', x: payload.x, y: payload.y }}
      closeAll={closeAndRestore}
      level={0}
      ariaLabel={payload.ariaLabel}
      autoFocusFirst={false}
    />,
    document.body,
  );
}
